import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { DocumentUploadPanel } from '../components/DocumentUploadPanel';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { 
    FileText, 
    Plus, 
    Eye, 
    Filter,
    AlertTriangle,
    CheckCircle,
    Clock,
    XCircle,
    Search,
    Download,
    RefreshCw,
    Ship,
    Truck,
    Upload,
    History,
    BarChart3,
    Calendar,
    FileWarning,
    Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';

const API = `${window.location.origin}/api`;

const tipoDocumentoLabels = {
    tie: 'TIE - Título de Inscrição',
    cts: 'CTS - Certificado de Tripulação',
    certificado_seguranca: 'Certificado de Segurança da Navegação',
    registro: 'Registro',
    certificado: 'Certificado',
    licenca: 'Licença',
    vistoria: 'Vistoria',
    seguro: 'Seguro',
    arqueacao: 'Arqueação',
    classificacao: 'Classificação',
    antaq: 'ANTAQ',
    outros: 'Outros'
};

const orgaoEmissorLabels = {
    marinha_brasil: 'Marinha do Brasil',
    antaq: 'ANTAQ',
    classificadora: 'Sociedade Classificadora',
    ibama: 'IBAMA',
    receita_federal: 'Receita Federal',
    capitania_portos: 'Capitania dos Portos',
    outros: 'Outros'
};

const periodicidadeLabels = {
    mensal: 'Mensal',
    trimestral: 'Trimestral',
    semestral: 'Semestral',
    anual: 'Anual',
    bienal: 'Bienal',
    eventual: 'Eventual',
    permanente: 'Permanente'
};

const statusLabels = {
    vigente: { label: 'Vigente', class: 'badge-success', icon: CheckCircle, color: '#22c55e' },
    a_vencer: { label: 'A Vencer', class: 'badge-warning', icon: Clock, color: '#f59e0b' },
    vencido: { label: 'Vencido', class: 'badge-danger', icon: XCircle, color: '#ef4444' },
    inativo: { label: 'Inativo', class: 'badge-secondary', icon: FileWarning, color: '#64748b' }
};

const DocumentosBordoPage = () => {
    const [documentos, setDocumentos] = useState([]);
    const [empurradores, setEmpurradores] = useState([]);
    const [balsas, setBalsas] = useState([]);
    const [dashboard, setDashboard] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [renovarDialogOpen, setRenovarDialogOpen] = useState(false);
    const [selectedDocumento, setSelectedDocumento] = useState(null);
    const { hasRole } = useAuth();

    const [filters, setFilters] = useState({
        embarcacao_tipo: '',
        embarcacao_id: '',
        tipo: '',
        status: '',
        busca: ''
    });

    const [formData, setFormData] = useState({
        nome: '',
        tipo: '',
        numero: '',
        orgao_emissor: '',
        orgao_emissor_outro: '',
        embarcacao_tipo: '',
        embarcacao_id: '',
        data_emissao: '',
        data_validade: '',
        periodicidade: 'anual',
        dias_alerta: 30,
        observacoes: ''
    });

    const [renovacaoData, setRenovacaoData] = useState({
        data_emissao: '',
        data_validade: '',
        numero: '',
        observacoes: ''
    });

    const fetchBaseData = useCallback(async () => {
        try {
            const [empRes, balsasRes] = await Promise.all([
                axios.get(`${API}/empurradores`),
                axios.get(`${API}/balsas`)
            ]);
            setEmpurradores(empRes.data);
            setBalsas(balsasRes.data);
        } catch (error) {
            console.error('Erro ao carregar dados base:', error);
        }
    }, []);

    const fetchDocumentos = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (filters.embarcacao_tipo && filters.embarcacao_tipo !== 'all') params.embarcacao_tipo = filters.embarcacao_tipo;
            if (filters.embarcacao_id && filters.embarcacao_id !== 'all') params.embarcacao_id = filters.embarcacao_id;
            if (filters.tipo && filters.tipo !== 'all') params.tipo = filters.tipo;
            if (filters.status && filters.status !== 'all') params.status = filters.status;
            if (filters.busca) params.busca = filters.busca;

            const response = await axios.get(`${API}/documentos-embarcacao`, { params });
            setDocumentos(response.data);
        } catch (error) {
            toast.error('Erro ao carregar documentos');
        } finally {
            setLoading(false);
        }
    }, [filters]);

    const fetchDashboard = useCallback(async () => {
        try {
            const params = {};
            if (filters.embarcacao_tipo && filters.embarcacao_tipo !== 'all') params.embarcacao_tipo = filters.embarcacao_tipo;
            if (filters.embarcacao_id && filters.embarcacao_id !== 'all') params.embarcacao_id = filters.embarcacao_id;
            
            const response = await axios.get(`${API}/documentos-embarcacao/dashboard`, { params });
            setDashboard(response.data);
        } catch (error) {
            console.error('Erro ao carregar dashboard:', error);
        }
    }, [filters.embarcacao_tipo, filters.embarcacao_id]);

    useEffect(() => {
        fetchBaseData();
    }, [fetchBaseData]);

    useEffect(() => {
        fetchDocumentos();
        fetchDashboard();
    }, [fetchDocumentos, fetchDashboard]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.nome || !formData.tipo || !formData.embarcacao_tipo || !formData.embarcacao_id) {
            toast.error('Preencha todos os campos obrigatórios');
            return;
        }

        try {
            await axios.post(`${API}/documentos-embarcacao`, formData);
            toast.success('Documento cadastrado com sucesso!');
            setDialogOpen(false);
            resetForm();
            fetchDocumentos();
            fetchDashboard();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao cadastrar documento');
        }
    };

    const handleRenovar = async () => {
        if (!renovacaoData.data_validade) {
            toast.error('Informe a nova data de validade');
            return;
        }

        try {
            await axios.put(`${API}/documentos-embarcacao/${selectedDocumento.id}/renovar`, renovacaoData);
            toast.success('Documento renovado com sucesso!');
            setRenovarDialogOpen(false);
            setRenovacaoData({ data_emissao: '', data_validade: '', numero: '', observacoes: '' });
            fetchDocumentos();
            fetchDashboard();
        } catch (error) {
            toast.error('Erro ao renovar documento');
        }
    };

    const handleInativar = async (docId) => {
        if (!window.confirm('Tem certeza que deseja EXCLUIR este documento?\n\nEsta ação não pode ser desfeita.')) return;

        try {
            await axios.delete(`${API}/documentos-embarcacao/${docId}`);
            toast.success('Documento excluído com sucesso');
            fetchDocumentos();
            fetchDashboard();
        } catch (error) {
            toast.error('Erro ao excluir documento');
        }
    };

    const resetForm = () => {
        setFormData({
            nome: '',
            tipo: '',
            numero: '',
            orgao_emissor: '',
            orgao_emissor_outro: '',
            embarcacao_tipo: '',
            embarcacao_id: '',
            data_emissao: '',
            data_validade: '',
            periodicidade: 'anual',
            dias_alerta: 30,
            observacoes: ''
        });
    };

    const getEmbarcacaoNome = (tipo, id) => {
        if (tipo === 'empurrador') {
            return empurradores.find(e => e.id === id)?.nome || '-';
        } else if (tipo === 'balsa') {
            return balsas.find(b => b.id === id)?.nome || '-';
        }
        return '-';
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr + 'T00:00:00').toLocaleDateString('pt-BR');
    };

    const getDiasRestantes = (dataValidade) => {
        if (!dataValidade) return null;
        const validade = new Date(dataValidade + 'T00:00:00');
        const hoje = new Date();
        hoje.setHours(0, 0, 0, 0);
        const diff = Math.ceil((validade - hoje) / (1000 * 60 * 60 * 24));
        return diff;
    };

    const canEdit = hasRole(['admin', 'gestor', 'operador']);
    const canDelete = hasRole(['admin']);

    // Dados para gráfico de pizza
    const pieData = dashboard ? [
        { name: 'Vigentes', value: dashboard.vigentes, color: '#22c55e' },
        { name: 'A Vencer', value: dashboard.a_vencer, color: '#f59e0b' },
        { name: 'Vencidos', value: dashboard.vencidos, color: '#ef4444' }
    ].filter(d => d.value > 0) : [];

    if (loading && documentos.length === 0) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-pulse text-cyan-400">Carregando...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="documentos-bordo-page">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="font-heading text-2xl font-bold text-white tracking-tight">
                        Controle de Documentos de Bordo
                    </h1>
                    <p className="text-sm text-slate-400 mt-1">
                        Gestão, rastreabilidade e controle de vencimentos
                    </p>
                </div>
                
                {canEdit && (
                    <Dialog open={dialogOpen} onOpenChange={(open) => {
                        setDialogOpen(open);
                        if (!open) resetForm();
                    }}>
                        <DialogTrigger asChild>
                            <Button 
                                data-testid="btn-new-documento"
                                className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-sm"
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                Novo Documento
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-slate-900 border-slate-800 rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto">
                            <DialogHeader>
                                <DialogTitle className="font-heading text-xl text-white flex items-center gap-2">
                                    <FileText className="w-5 h-5 text-cyan-400" />
                                    Cadastrar Documento de Bordo
                                </DialogTitle>
                            </DialogHeader>
                            
                            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
                                {/* Embarcação */}
                                <div className="p-4 bg-slate-800/50 rounded-sm space-y-4">
                                    <h3 className="text-sm font-mono uppercase tracking-wider text-cyan-400">
                                        Embarcação
                                    </h3>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                                Tipo de Embarcação *
                                            </Label>
                                            <Select 
                                                value={formData.embarcacao_tipo || "none"} 
                                                onValueChange={(value) => setFormData({
                                                    ...formData, 
                                                    embarcacao_tipo: value === "none" ? "" : value,
                                                    embarcacao_id: ''
                                                })}
                                            >
                                                <SelectTrigger className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm">
                                                    <SelectValue placeholder="Selecione..." />
                                                </SelectTrigger>
                                                <SelectContent className="bg-slate-900 border-slate-800">
                                                    <SelectItem value="none">Selecione...</SelectItem>
                                                    <SelectItem value="empurrador">Empurrador</SelectItem>
                                                    <SelectItem value="balsa">Balsa</SelectItem>
                                                    <SelectItem value="rebocador">Rebocador</SelectItem>
                                                    <SelectItem value="apoio">Apoio</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        
                                        {formData.embarcacao_tipo && (
                                            <div className="space-y-2">
                                                <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                                    Embarcação *
                                                </Label>
                                                <Select 
                                                    value={formData.embarcacao_id || "none"} 
                                                    onValueChange={(value) => setFormData({...formData, embarcacao_id: value === "none" ? "" : value})}
                                                >
                                                    <SelectTrigger className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm">
                                                        <SelectValue placeholder="Selecione..." />
                                                    </SelectTrigger>
                                                    <SelectContent className="bg-slate-900 border-slate-800">
                                                        <SelectItem value="none">Selecione...</SelectItem>
                                                        {formData.embarcacao_tipo === 'empurrador' && empurradores.map((emp) => (
                                                            <SelectItem key={emp.id} value={emp.id}>{emp.nome}</SelectItem>
                                                        ))}
                                                        {formData.embarcacao_tipo === 'balsa' && balsas.map((b) => (
                                                            <SelectItem key={b.id} value={b.id}>{b.nome}</SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Documento */}
                                <div className="p-4 bg-slate-800/50 rounded-sm space-y-4">
                                    <h3 className="text-sm font-mono uppercase tracking-wider text-cyan-400">
                                        Dados do Documento
                                    </h3>
                                    
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                                Tipo do Documento *
                                            </Label>
                                            <Select 
                                                value={formData.tipo || "none"} 
                                                onValueChange={(value) => setFormData({...formData, tipo: value === "none" ? "" : value})}
                                            >
                                                <SelectTrigger className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm">
                                                    <SelectValue placeholder="Selecione..." />
                                                </SelectTrigger>
                                                <SelectContent className="bg-slate-900 border-slate-800">
                                                    <SelectItem value="none">Selecione...</SelectItem>
                                                    {Object.entries(tipoDocumentoLabels).map(([key, label]) => (
                                                        <SelectItem key={key} value={key}>{label}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        
                                        <div className="space-y-2">
                                            <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                                Nome/Descrição *
                                            </Label>
                                            <Input
                                                value={formData.nome}
                                                onChange={(e) => setFormData({...formData, nome: e.target.value})}
                                                required
                                                placeholder="Ex: Certificado de Segurança 2026"
                                                className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                                Número do Documento
                                            </Label>
                                            <Input
                                                value={formData.numero}
                                                onChange={(e) => setFormData({...formData, numero: e.target.value})}
                                                placeholder="Número/Registro"
                                                className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                            />
                                        </div>
                                        
                                        <div className="space-y-2">
                                            <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                                Órgão Emissor
                                            </Label>
                                            <Select 
                                                value={formData.orgao_emissor || "none"} 
                                                onValueChange={(value) => setFormData({...formData, orgao_emissor: value === "none" ? "" : value})}
                                            >
                                                <SelectTrigger className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm">
                                                    <SelectValue placeholder="Selecione..." />
                                                </SelectTrigger>
                                                <SelectContent className="bg-slate-900 border-slate-800">
                                                    <SelectItem value="none">Selecione...</SelectItem>
                                                    {Object.entries(orgaoEmissorLabels).map(([key, label]) => (
                                                        <SelectItem key={key} value={key}>{label}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                </div>

                                {/* Validade */}
                                <div className="p-4 bg-slate-800/50 rounded-sm space-y-4">
                                    <h3 className="text-sm font-mono uppercase tracking-wider text-cyan-400">
                                        Validade
                                    </h3>
                                    
                                    <div className="grid grid-cols-3 gap-4">
                                        <div className="space-y-2">
                                            <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                                Data de Emissão
                                            </Label>
                                            <Input
                                                type="date"
                                                value={formData.data_emissao}
                                                onChange={(e) => setFormData({...formData, data_emissao: e.target.value})}
                                                className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                            />
                                        </div>
                                        
                                        <div className="space-y-2">
                                            <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                                Data de Validade
                                            </Label>
                                            <Input
                                                type="date"
                                                value={formData.data_validade}
                                                onChange={(e) => setFormData({...formData, data_validade: e.target.value})}
                                                className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                            />
                                        </div>
                                        
                                        <div className="space-y-2">
                                            <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                                Periodicidade
                                            </Label>
                                            <Select 
                                                value={formData.periodicidade} 
                                                onValueChange={(value) => setFormData({...formData, periodicidade: value})}
                                            >
                                                <SelectTrigger className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent className="bg-slate-900 border-slate-800">
                                                    {Object.entries(periodicidadeLabels).map(([key, label]) => (
                                                        <SelectItem key={key} value={key}>{label}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                            Dias para Alerta de Vencimento
                                        </Label>
                                        <Select 
                                            value={formData.dias_alerta.toString()} 
                                            onValueChange={(value) => setFormData({...formData, dias_alerta: parseInt(value)})}
                                        >
                                            <SelectTrigger className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm w-40">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="bg-slate-900 border-slate-800">
                                                <SelectItem value="30">30 dias</SelectItem>
                                                <SelectItem value="60">60 dias</SelectItem>
                                                <SelectItem value="90">90 dias</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>

                                {/* Observações */}
                                <div className="space-y-2">
                                    <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                        Observações
                                    </Label>
                                    <Textarea
                                        value={formData.observacoes}
                                        onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
                                        placeholder="Observações adicionais..."
                                        className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm min-h-[60px]"
                                    />
                                </div>

                                <div className="flex gap-3 pt-4">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => {
                                            setDialogOpen(false);
                                            resetForm();
                                        }}
                                        className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800 rounded-sm"
                                    >
                                        Cancelar
                                    </Button>
                                    <Button
                                        type="submit"
                                        className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white rounded-sm"
                                    >
                                        Cadastrar Documento
                                    </Button>
                                </div>
                            </form>
                        </DialogContent>
                    </Dialog>
                )}
            </div>

            {/* Dashboard Cards */}
            {dashboard && (
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                        <CardContent className="flex items-center gap-4 p-4">
                            <div className="p-2 bg-blue-500/20 rounded-sm">
                                <FileText className="w-5 h-5 text-blue-400" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-white">{dashboard.total}</p>
                                <p className="text-xs text-slate-400">Total Documentos</p>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                        <CardContent className="flex items-center gap-4 p-4">
                            <div className="p-2 bg-green-500/20 rounded-sm">
                                <CheckCircle className="w-5 h-5 text-green-400" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-green-400">{dashboard.vigentes}</p>
                                <p className="text-xs text-slate-400">Vigentes</p>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                        <CardContent className="flex items-center gap-4 p-4">
                            <div className="p-2 bg-yellow-500/20 rounded-sm">
                                <Clock className="w-5 h-5 text-yellow-400" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-yellow-400">{dashboard.a_vencer}</p>
                                <p className="text-xs text-slate-400">A Vencer</p>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                        <CardContent className="flex items-center gap-4 p-4">
                            <div className="p-2 bg-red-500/20 rounded-sm">
                                <XCircle className="w-5 h-5 text-red-400" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-red-400">{dashboard.vencidos}</p>
                                <p className="text-xs text-slate-400">Vencidos</p>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className={`rounded-sm border ${
                        dashboard.percentual_conformidade_geral >= 90 ? 'bg-green-500/10 border-green-500/30' :
                        dashboard.percentual_conformidade_geral >= 70 ? 'bg-yellow-500/10 border-yellow-500/30' :
                        'bg-red-500/10 border-red-500/30'
                    }`}>
                        <CardContent className="flex items-center gap-4 p-4">
                            <div className="p-2 bg-slate-900/50 rounded-sm">
                                <BarChart3 className={`w-5 h-5 ${
                                    dashboard.percentual_conformidade_geral >= 90 ? 'text-green-400' :
                                    dashboard.percentual_conformidade_geral >= 70 ? 'text-yellow-400' :
                                    'text-red-400'
                                }`} />
                            </div>
                            <div>
                                <p className={`text-2xl font-bold ${
                                    dashboard.percentual_conformidade_geral >= 90 ? 'text-green-400' :
                                    dashboard.percentual_conformidade_geral >= 70 ? 'text-yellow-400' :
                                    'text-red-400'
                                }`}>{dashboard.percentual_conformidade_geral}%</p>
                                <p className="text-xs text-slate-400">Conformidade</p>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Alertas */}
            {dashboard && dashboard.alertas && dashboard.alertas.length > 0 && (
                <Card className="bg-red-500/10 border-red-500/30 rounded-sm">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-mono uppercase tracking-wider text-red-400 flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4" />
                            Alertas de Documentos ({dashboard.alertas.length})
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {dashboard.alertas.slice(0, 6).map((doc) => (
                                <div key={doc.id} className={`p-3 rounded-sm border ${
                                    doc.status === 'vencido' ? 'bg-red-500/10 border-red-500/30' : 'bg-yellow-500/10 border-yellow-500/30'
                                }`}>
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <p className="text-sm font-medium text-white">{doc.nome}</p>
                                            <p className="text-xs text-slate-400">
                                                {getEmbarcacaoNome(doc.embarcacao_tipo, doc.embarcacao_id)}
                                            </p>
                                        </div>
                                        <span className={`text-xs font-mono px-2 py-0.5 rounded ${
                                            doc.status === 'vencido' ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'
                                        }`}>
                                            {doc.status === 'vencido' ? 'VENCIDO' : `${getDiasRestantes(doc.data_validade)} dias`}
                                        </span>
                                    </div>
                                    <p className="text-xs text-slate-500 mt-1">
                                        Validade: {formatDate(doc.data_validade)}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Gráfico e Vencimentos */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Gráfico de Pizza */}
                {pieData.length > 0 && (
                    <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-mono uppercase tracking-wider text-cyan-400">
                                Distribuição por Status
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <ResponsiveContainer width="100%" height={200}>
                                <PieChart>
                                    <Pie
                                        data={pieData}
                                        dataKey="value"
                                        nameKey="name"
                                        cx="50%"
                                        cy="50%"
                                        outerRadius={70}
                                        label={({ name, value }) => `${value}`}
                                    >
                                        {pieData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        </CardContent>
                    </Card>
                )}

                {/* Vencimentos por Período */}
                {dashboard && (
                    <Card className="bg-slate-900/50 border-slate-800 rounded-sm col-span-2">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-mono uppercase tracking-wider text-cyan-400 flex items-center gap-2">
                                <Calendar className="w-4 h-4" />
                                Vencimentos por Período
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-3 gap-4">
                                <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-sm text-center">
                                    <p className="text-3xl font-bold text-yellow-400">{dashboard.vencer_30_dias}</p>
                                    <p className="text-xs text-slate-400">Próximos 30 dias</p>
                                </div>
                                <div className="p-4 bg-orange-500/10 border border-orange-500/30 rounded-sm text-center">
                                    <p className="text-3xl font-bold text-orange-400">{dashboard.vencer_60_dias}</p>
                                    <p className="text-xs text-slate-400">31-60 dias</p>
                                </div>
                                <div className="p-4 bg-slate-800/50 border border-slate-700 rounded-sm text-center">
                                    <p className="text-3xl font-bold text-slate-300">{dashboard.vencer_90_dias}</p>
                                    <p className="text-xs text-slate-400">61-90 dias</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>

            {/* Filtros */}
            <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-4">
                        <Filter className="w-4 h-4 text-cyan-400" />
                        <span className="text-sm font-mono uppercase tracking-wider text-cyan-400">Filtros</span>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                        <div className="space-y-1">
                            <Label className="text-slate-400 text-xs">Busca</Label>
                            <div className="relative">
                                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500" />
                                <Input
                                    placeholder="Nome ou número..."
                                    value={filters.busca}
                                    onChange={(e) => setFilters({...filters, busca: e.target.value})}
                                    className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm h-9 pl-9"
                                />
                            </div>
                        </div>
                        <div className="space-y-1">
                            <Label className="text-slate-400 text-xs">Tipo Embarcação</Label>
                            <Select 
                                value={filters.embarcacao_tipo || "all"} 
                                onValueChange={(value) => setFilters({...filters, embarcacao_tipo: value === "all" ? "" : value, embarcacao_id: ''})}
                            >
                                <SelectTrigger className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm h-9">
                                    <SelectValue placeholder="Todos" />
                                </SelectTrigger>
                                <SelectContent className="bg-slate-900 border-slate-800">
                                    <SelectItem value="all">Todos</SelectItem>
                                    <SelectItem value="empurrador">Empurrador</SelectItem>
                                    <SelectItem value="balsa">Balsa</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        {filters.embarcacao_tipo && (
                            <div className="space-y-1">
                                <Label className="text-slate-400 text-xs">Embarcação</Label>
                                <Select 
                                    value={filters.embarcacao_id || "all"} 
                                    onValueChange={(value) => setFilters({...filters, embarcacao_id: value === "all" ? "" : value})}
                                >
                                    <SelectTrigger className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm h-9">
                                        <SelectValue placeholder="Todas" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-slate-900 border-slate-800">
                                        <SelectItem value="all">Todas</SelectItem>
                                        {filters.embarcacao_tipo === 'empurrador' && empurradores.map((emp) => (
                                            <SelectItem key={emp.id} value={emp.id}>{emp.nome}</SelectItem>
                                        ))}
                                        {filters.embarcacao_tipo === 'balsa' && balsas.map((b) => (
                                            <SelectItem key={b.id} value={b.id}>{b.nome}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        <div className="space-y-1">
                            <Label className="text-slate-400 text-xs">Tipo Documento</Label>
                            <Select 
                                value={filters.tipo || "all"} 
                                onValueChange={(value) => setFilters({...filters, tipo: value === "all" ? "" : value})}
                            >
                                <SelectTrigger className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm h-9">
                                    <SelectValue placeholder="Todos" />
                                </SelectTrigger>
                                <SelectContent className="bg-slate-900 border-slate-800">
                                    <SelectItem value="all">Todos</SelectItem>
                                    {Object.entries(tipoDocumentoLabels).map(([key, label]) => (
                                        <SelectItem key={key} value={key}>{label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1">
                            <Label className="text-slate-400 text-xs">Status</Label>
                            <Select 
                                value={filters.status || "all"} 
                                onValueChange={(value) => setFilters({...filters, status: value === "all" ? "" : value})}
                            >
                                <SelectTrigger className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm h-9">
                                    <SelectValue placeholder="Todos" />
                                </SelectTrigger>
                                <SelectContent className="bg-slate-900 border-slate-800">
                                    <SelectItem value="all">Todos</SelectItem>
                                    <SelectItem value="vigente">🟢 Vigente</SelectItem>
                                    <SelectItem value="a_vencer">🟡 A Vencer</SelectItem>
                                    <SelectItem value="vencido">🔴 Vencido</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex items-end">
                            <Button 
                                onClick={() => setFilters({ embarcacao_tipo: '', embarcacao_id: '', tipo: '', status: '', busca: '' })}
                                variant="outline"
                                className="w-full border-slate-700 text-slate-300 hover:bg-slate-800 rounded-sm h-9"
                            >
                                Limpar
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Table */}
            <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                <CardContent className="p-0">
                    {documentos.length === 0 ? (
                        <div className="p-12 text-center">
                            <FileText className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                            <p className="text-slate-400">Nenhum documento encontrado</p>
                        </div>
                    ) : (
                        <Table className="table-naval">
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Documento</TableHead>
                                    <TableHead>Embarcação</TableHead>
                                    <TableHead>Número</TableHead>
                                    <TableHead>Órgão</TableHead>
                                    <TableHead>Validade</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Ações</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {documentos.map((doc) => {
                                    const diasRestantes = getDiasRestantes(doc.data_validade);
                                    const StatusIcon = statusLabels[doc.status]?.icon || FileText;
                                    
                                    return (
                                        <TableRow key={doc.id}>
                                            <TableCell>
                                                <div>
                                                    <p className="text-white font-medium">{doc.nome}</p>
                                                    <p className="text-xs text-slate-500">{tipoDocumentoLabels[doc.tipo] || doc.tipo}</p>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    {doc.embarcacao_tipo === 'empurrador' ? (
                                                        <Truck className="w-4 h-4 text-slate-500" />
                                                    ) : (
                                                        <Ship className="w-4 h-4 text-slate-500" />
                                                    )}
                                                    <span className="text-slate-300">
                                                        {getEmbarcacaoNome(doc.embarcacao_tipo, doc.embarcacao_id)}
                                                    </span>
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-slate-400 font-mono text-sm">
                                                {doc.numero || '-'}
                                            </TableCell>
                                            <TableCell className="text-slate-400 text-sm">
                                                {orgaoEmissorLabels[doc.orgao_emissor] || doc.orgao_emissor || '-'}
                                            </TableCell>
                                            <TableCell>
                                                <div>
                                                    <p className="text-slate-300">{formatDate(doc.data_validade)}</p>
                                                    {diasRestantes !== null && (
                                                        <p className={`text-xs ${
                                                            diasRestantes < 0 ? 'text-red-400' :
                                                            diasRestantes <= 30 ? 'text-yellow-400' :
                                                            'text-slate-500'
                                                        }`}>
                                                            {diasRestantes < 0 ? `Vencido há ${Math.abs(diasRestantes)} dias` :
                                                             diasRestantes === 0 ? 'Vence hoje' :
                                                             `${diasRestantes} dias restantes`}
                                                        </p>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <span className={`
                                                    inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded-sm
                                                    ${statusLabels[doc.status]?.class || 'badge-info'}
                                                `}>
                                                    <StatusIcon className="w-3 h-3" />
                                                    {statusLabels[doc.status]?.label || doc.status}
                                                </span>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex gap-1">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => {
                                                            setSelectedDocumento(doc);
                                                            setDetailDialogOpen(true);
                                                        }}
                                                        className="text-slate-400 hover:text-white hover:bg-slate-800"
                                                        title="Ver detalhes"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </Button>
                                                    {canEdit && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => {
                                                                setSelectedDocumento(doc);
                                                                setRenovacaoData({
                                                                    data_emissao: new Date().toISOString().split('T')[0],
                                                                    data_validade: '',
                                                                    numero: doc.numero || '',
                                                                    observacoes: ''
                                                                });
                                                                setRenovarDialogOpen(true);
                                                            }}
                                                            className="text-cyan-400 hover:text-cyan-300 hover:bg-slate-800"
                                                            title="Renovar documento"
                                                        >
                                                            <RefreshCw className="w-4 h-4" />
                                                        </Button>
                                                    )}
                                                    {canDelete && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleInativar(doc.id)}
                                                            className="text-red-400 hover:text-red-300 hover:bg-slate-800"
                                                            title="Excluir documento"
                                                            data-testid={`btn-delete-documento-${doc.id}`}
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </Button>
                                                    )}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* Detail Dialog */}
            <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
                <DialogContent className="bg-slate-900 border-slate-800 rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl text-white flex items-center gap-2">
                            <FileText className="w-5 h-5 text-cyan-400" />
                            Detalhes do Documento
                        </DialogTitle>
                    </DialogHeader>
                    
                    {selectedDocumento && (
                        <div className="space-y-6 mt-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-xs text-slate-500 font-mono uppercase">Nome</p>
                                    <p className="text-white mt-1">{selectedDocumento.nome}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500 font-mono uppercase">Tipo</p>
                                    <p className="text-white mt-1">{tipoDocumentoLabels[selectedDocumento.tipo]}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500 font-mono uppercase">Embarcação</p>
                                    <p className="text-white mt-1">{getEmbarcacaoNome(selectedDocumento.embarcacao_tipo, selectedDocumento.embarcacao_id)}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500 font-mono uppercase">Número</p>
                                    <p className="text-white mt-1">{selectedDocumento.numero || '-'}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500 font-mono uppercase">Data Emissão</p>
                                    <p className="text-white mt-1">{formatDate(selectedDocumento.data_emissao)}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500 font-mono uppercase">Data Validade</p>
                                    <p className="text-white mt-1">{formatDate(selectedDocumento.data_validade)}</p>
                                </div>
                            </div>

                            {/* Histórico */}
                            {selectedDocumento.historico && selectedDocumento.historico.length > 0 && (
                                <div>
                                    <p className="text-xs text-slate-500 font-mono uppercase mb-2 flex items-center gap-2">
                                        <History className="w-4 h-4" /> Histórico de Alterações
                                    </p>
                                    <div className="space-y-2 max-h-[200px] overflow-y-auto">
                                        {selectedDocumento.historico.map((hist, idx) => (
                                            <div key={idx} className="text-xs bg-slate-800/50 p-2 rounded-sm">
                                                <span className="text-slate-500">{new Date(hist.data).toLocaleString('pt-BR')}</span>
                                                <span className="text-slate-400 mx-2">|</span>
                                                <span className="text-cyan-400">{hist.usuario}</span>
                                                <span className="text-slate-400 mx-2">-</span>
                                                <span className="text-white">{hist.acao}</span>
                                                {hist.campo_alterado && (
                                                    <>
                                                        <span className="text-slate-400">: {hist.campo_alterado}</span>
                                                        <span className="text-red-400 mx-1">{hist.valor_anterior}</span>
                                                        <span className="text-slate-400">→</span>
                                                        <span className="text-green-400 mx-1">{hist.valor_novo}</span>
                                                    </>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Upload de Arquivo do Documento */}
                            {selectedDocumento && (
                                <DocumentUploadPanel modulo="embarcacoes" entidadeId={selectedDocumento.id} entidadeNome={selectedDocumento.nome} />
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Renovar Dialog */}
            <Dialog open={renovarDialogOpen} onOpenChange={setRenovarDialogOpen}>
                <DialogContent className="bg-slate-900 border-slate-800 rounded-sm max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl text-white flex items-center gap-2">
                            <RefreshCw className="w-5 h-5 text-cyan-400" />
                            Renovar Documento
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4 mt-4">
                        <p className="text-sm text-slate-400">
                            Documento: <span className="text-white">{selectedDocumento?.nome}</span>
                        </p>
                        
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                    Nova Data Emissão
                                </Label>
                                <Input
                                    type="date"
                                    value={renovacaoData.data_emissao}
                                    onChange={(e) => setRenovacaoData({...renovacaoData, data_emissao: e.target.value})}
                                    className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                    Nova Validade *
                                </Label>
                                <Input
                                    type="date"
                                    value={renovacaoData.data_validade}
                                    onChange={(e) => setRenovacaoData({...renovacaoData, data_validade: e.target.value})}
                                    required
                                    className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                Novo Número (se alterado)
                            </Label>
                            <Input
                                value={renovacaoData.numero}
                                onChange={(e) => setRenovacaoData({...renovacaoData, numero: e.target.value})}
                                className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                Observações
                            </Label>
                            <Textarea
                                value={renovacaoData.observacoes}
                                onChange={(e) => setRenovacaoData({...renovacaoData, observacoes: e.target.value})}
                                placeholder="Observações sobre a renovação..."
                                className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm min-h-[60px]"
                            />
                        </div>

                        <div className="flex gap-3 pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setRenovarDialogOpen(false)}
                                className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800 rounded-sm"
                            >
                                Cancelar
                            </Button>
                            <Button
                                onClick={handleRenovar}
                                className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white rounded-sm"
                            >
                                Renovar
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default DocumentosBordoPage;
